import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { GitCompareArrows, Loader2 } from "lucide-react";
import { computeReportStats, FOLLOWER_TIERS, formatNum, fmtPct } from "../components/followerTiers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTION_DEFS = [
  { id: "overview", label: "Overview" },
  { id: "engagement", label: "Engagement totals" },
  { id: "reach", label: "Reach" },
  { id: "er", label: "Engagement rate" },
  { id: "deliverable", label: "Deliverable type" },
  { id: "bif", label: "Posted vs Not-Posted" },
  { id: "tiers", label: "Creator tiers" },
];

const DashTile = ({ label, value, sub, tone = "text-slate-900", testId }) => (
  <div
    className="rounded-xl bg-white border border-slate-200/80 px-3 py-2.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:border-indigo-200 transition-[border-color] duration-150"
    data-testid={testId}
  >
    <div className="text-[9px] font-mono uppercase tracking-wider text-slate-400">{label}</div>
    <div className={`text-lg font-semibold font-mono tabular-nums tracking-tight ${tone} mt-0.5`}>{value}</div>
    {sub && <div className="text-[9px] font-mono text-slate-400 mt-0.5 leading-snug">{sub}</div>}
  </div>
);

const SectionTitle = ({ children }) => (
  <div className="text-[9px] font-mono uppercase tracking-widest text-slate-400 mt-4 mb-1.5 first:mt-0">
    {children}
  </div>
);

/** One report rendered as a mini Stats & ER dashboard. */
const MiniDashboard = ({ name, stats, summary, vis, testId }) => {
  // ER strip — per-creator method (same as the report's bifurcation strip).
  const strip = useMemo(() => {
    const r = summary.map((s) => Number(s.engagement_rate_reels)).filter((v) => Number.isFinite(v) && v > 0);
    const st = summary.map((s) => Number(s.engagement_rate_static)).filter((v) => Number.isFinite(v) && v > 0);
    const nPosted = summary.filter((s) => (s.matched_posts || 0) > 0).length;
    return {
      reels: r.length ? r.reduce((a, b) => a + b, 0) / r.length : null,
      static: st.length ? st.reduce((a, b) => a + b, 0) / st.length : null,
      overall: nPosted ? (r.reduce((a, b) => a + b, 0) + st.reduce((a, b) => a + b, 0)) / nPosted : null,
    };
  }, [summary]);

  const { counts, bif, tiers } = stats;
  const on = (k) => vis[k] !== false;
  const fr = (n) => `${n} / ${bif.total}`;

  return (
    <div
      className="rounded-2xl border border-slate-200/80 bg-slate-50/50 p-4 shadow-[0_1px_2px_rgba(15,23,42,0.03)]"
      data-testid={testId}
    >
      <div className="flex items-baseline gap-2 mb-3 px-1">
        <h2 className="text-sm font-semibold text-slate-900 tracking-tight truncate" title={name}>{name}</h2>
        <span className="text-[10px] font-mono text-slate-400 shrink-0">{bif.total} creators</span>
      </div>

      {on("overview") && (
        <>
          <SectionTitle>Overview</SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <DashTile label="Total" value={formatNum(stats.grandTotal)} sub={`${formatNum(stats.postTotal)} posts + ${counts.live} live`} testId={`${testId}-total`} />
            <DashTile label="Reels" value={formatNum(counts.reels)} testId={`${testId}-reels`} />
            <DashTile label="Posts" value={formatNum(counts.static)} />
            <DashTile label="Carousels" value={formatNum(counts.carousels)} />
            <DashTile label="Collabs" value={formatNum(counts.collabs)} sub="highlight — already inside type counts" />
            <DashTile label="Live 24h" value={formatNum(counts.live)} />
            <DashTile label="Highlights" value={formatNum(counts.highlights)} sub="not in Total" />
          </div>
        </>
      )}

      {on("engagement") && (
        <>
          <SectionTitle>Engagement totals</SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <DashTile label="Total Views" value={formatNum(stats.totals.views)} tone="text-cyan-700" testId={`${testId}-views`} />
            <DashTile label="Total Likes" value={formatNum(stats.totals.likes)} tone="text-pink-700" />
            <DashTile label="Total Comments" value={formatNum(stats.totals.comments)} tone="text-violet-700" />
            <DashTile label="Total Shares" value={stats.totals.hasShares ? formatNum(stats.totals.shares) : "—"} tone="text-emerald-700" />
          </div>
        </>
      )}

      {(on("reach") || on("er")) && (
        <>
          <SectionTitle>Reach & Engagement rate</SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {on("reach") && <DashTile label="Reach" value={formatNum(stats.reach)} tone="text-cyan-700" testId={`${testId}-reach`} />}
            {on("er") && <DashTile label="ER Reels" value={fmtPct(stats.erReels)} tone="text-pink-700" sub="avg per post" testId={`${testId}-er-reels`} />}
            {on("er") && <DashTile label="ER Static" value={fmtPct(stats.erStatic)} tone="text-emerald-700" sub="avg per post" testId={`${testId}-er-static`} />}
          </div>
        </>
      )}

      {on("deliverable") && (
        <>
          <SectionTitle>Deliverable type</SectionTitle>
          <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-[9px] uppercase tracking-wider font-mono text-slate-400 bg-slate-50/70 border-b border-slate-200">
                  <th className="text-left px-3 py-1.5 font-medium">Type</th>
                  <th className="text-right px-2 py-1.5 font-medium">Posts</th>
                  <th className="text-right px-2 py-1.5 font-medium">Views</th>
                  <th className="text-right px-2 py-1.5 font-medium">Likes</th>
                  <th className="text-right px-3 py-1.5 font-medium">Avg ER</th>
                </tr>
              </thead>
              <tbody>
                {stats.deliverables.map((d) => (
                  <tr key={d.type} className="border-b border-slate-100 last:border-0" data-testid={`${testId}-deliv-${d.type.toLowerCase().replace(/\s+/g, "-")}`}>
                    <td className="px-3 py-1.5 text-slate-700 font-medium">{d.type}</td>
                    <td className="text-right px-2 py-1.5 font-mono tabular-nums text-slate-800">{d.published}</td>
                    <td className="text-right px-2 py-1.5 font-mono tabular-nums text-slate-600">{formatNum(d.views)}</td>
                    <td className="text-right px-2 py-1.5 font-mono tabular-nums text-slate-600">{formatNum(d.likes)}</td>
                    <td className="text-right px-3 py-1.5 font-mono tabular-nums text-slate-900 font-semibold">{fmtPct(d.er)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {on("bif") && (
        <>
          <SectionTitle>Posted vs Not-Posted</SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <DashTile label="✓ Total Posted" value={fr(bif.posted)} tone="text-emerald-700" testId={`${testId}-posted`} />
            <DashTile label="Posted + Stories" value={fr(bif.postedWithStories)} tone="text-fuchsia-700" />
            <DashTile label="Posted only" value={fr(bif.postedOnly)} tone="text-violet-700" />
            <DashTile label="Stories only" value={fr(bif.storiesOnly)} tone="text-amber-700" />
            <DashTile label="✗ Nothing" value={fr(bif.nothing)} tone="text-rose-600" />
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <DashTile label="ER Reels · per creator" value={fmtPct(strip.reels)} tone="text-pink-700" testId={`${testId}-strip-er-reels`} />
            <DashTile label="ER Static · per creator" value={fmtPct(strip.static)} tone="text-emerald-700" testId={`${testId}-strip-er-static`} />
            <DashTile label="ER Overall" value={fmtPct(strip.overall)} tone="text-violet-700" testId={`${testId}-strip-er-overall`} />
          </div>
        </>
      )}

      {on("tiers") && (
        <>
          <SectionTitle>Creator tiers</SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {FOLLOWER_TIERS.map((t) => (
              <DashTile key={t.id} label={t.label} value={`${tiers[t.id] || 0}`} sub={t.hint} testId={`${testId}-tier-${t.id}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default function Compare() {
  const [reports, setReports] = useState([]);
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [dataA, setDataA] = useState(null);
  const [dataB, setDataB] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vis, setVis] = useState(() => {
    try { return JSON.parse(localStorage.getItem("cx-compare-vis") || "{}"); } catch { return {}; }
  });
  const toggle = (id) => setVis((prev) => {
    const next = { ...prev, [id]: prev[id] === false };
    try { localStorage.setItem("cx-compare-vis", JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  useEffect(() => {
    axios.get(`${API}/reports`).then(({ data }) => {
      const list = data.reports || [];
      setReports(list);
      if (list.length && !aId) setAId(list[0].id);
      if (list.length > 1 && !bId) setBId(list[1].id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!aId || !bId) return;
    let cancelled = false;
    setLoading(true);
    setDataA(null); setDataB(null);
    Promise.all([
      axios.get(`${API}/reports/${aId}/data`),
      axios.get(`${API}/reports/${bId}/data`),
    ]).then(([ra, rb]) => {
      if (cancelled) return;
      setDataA(ra.data);
      setDataB(rb.data);
      setLoading(false);
    }).catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [aId, bId]);

  const statsA = useMemo(() => dataA ? computeReportStats(dataA.posts || [], dataA.summary || []) : null, [dataA]);
  const statsB = useMemo(() => dataB ? computeReportStats(dataB.posts || [], dataB.summary || []) : null, [dataB]);
  const nameA = reports.find((r) => r.id === aId)?.name || "Report A";
  const nameB = reports.find((r) => r.id === bId)?.name || "Report B";

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="compare-page">
      <div className="flex items-center gap-2.5 mb-1">
        <GitCompareArrows size={18} className="text-indigo-600" />
        <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Compare Reports</h1>
      </div>
      <p className="text-xs text-slate-400 mb-5">
        Two full dashboards side by side. Toggle the sections you want to see on both.
      </p>

      {/* Pickers */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-center mb-4">
        <select
          value={aId}
          onChange={(e) => setAId(e.target.value)}
          className="w-full h-9 border border-slate-200 rounded-lg px-3 text-xs bg-white focus-brand"
          data-testid="compare-select-a"
        >
          {reports.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 text-center">vs</span>
        <select
          value={bId}
          onChange={(e) => setBId(e.target.value)}
          className="w-full h-9 border border-slate-200 rounded-lg px-3 text-xs bg-white focus-brand"
          data-testid="compare-select-b"
        >
          {reports.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </div>

      {/* Section toggles */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="compare-toggles">
        {SECTION_DEFS.map((s) => {
          const on = vis[s.id] !== false;
          return (
            <button
              key={s.id}
              onClick={() => toggle(s.id)}
              className={`px-3 py-1.5 rounded-full text-[11px] font-medium border transition-[background-color,border-color,color] duration-150 ${
                on
                  ? "bg-indigo-600 text-white border-indigo-600 shadow-sm"
                  : "bg-white text-slate-500 border-slate-200 hover:border-indigo-300 hover:text-indigo-600"
              }`}
              data-testid={`compare-toggle-${s.id}`}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="el-card p-12 text-center text-xs text-slate-400 font-mono" data-testid="compare-loading">
          <Loader2 size={16} className="inline-block animate-spin text-indigo-600 mb-2" />
          <div>Loading both reports…</div>
        </div>
      )}

      {!loading && statsA && statsB && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5" data-testid="compare-dashboards">
          <MiniDashboard name={nameA} stats={statsA} summary={dataA.summary || []} vis={vis} testId="compare-dash-a" />
          <MiniDashboard name={nameB} stats={statsB} summary={dataB.summary || []} vis={vis} testId="compare-dash-b" />
        </div>
      )}

      {!loading && !statsA && !statsB && reports.length === 0 && (
        <div className="el-card p-10 text-center text-xs font-mono text-slate-400" data-testid="compare-empty">
          No reports yet — create a report first.
        </div>
      )}
    </div>
  );
}
